import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { database } from '../../Firebase';
import { ref, push, onValue, remove, update } from 'firebase/database';
import { X } from 'lucide-react';

const AccountDetails = () => {
  const location = useLocation();
  const navigate = useNavigate();
  // eslint-disable-next-line no-unused-vars
  const { grampanchayatName, arthikVarshLabel } = location.state || {};

  const [view, setView] = useState('list'); // 'list' or 'form'
  const [searchTerm, setSearchTerm] = useState('');
  const [accountDetails, setAccountDetails] = useState([]);
  const [accountGroups, setAccountGroups] = useState([]);
  
  // Edit State
  const [isEditing, setIsEditing] = useState(false);
  const [selectedId, setSelectedId] = useState(null);

  // Modal State
  const [isGroupModalOpen, setIsGroupModalOpen] = useState(false);
  const [groupSearchTerm, setGroupSearchTerm] = useState('');

  // Form State
  const initialFormState = {
    type: 'मालमत्ता', // default
    accountNo: '',
    accountName: '',
    accountGroupId: '', 
    accountGroupName: '',
    transactionPermission: false,
    budgetEntry: false,
    
    // Permission Table Data
    panipuravthaPermission: false,
    openingBalance: '',
    balanceType: 'Credit' // Debit or Credit (checkbox logic simplified to type for now, or just amount)
  };

  const [formData, setFormData] = useState(initialFormState);

  // Fetch Data
  useEffect(() => {
    // Fetch Account Details
    const detailsRef = ref(database, 'Grampanchayat/Panipuravtha/AccountDetails');
    const unsubscribeDetails = onValue(detailsRef, (snapshot) => {
      const data = snapshot.val();
      if (data) {
        const loadDetails = Object.keys(data).map((key) => ({
          id: key,
          ...data[key],
        }));
        setAccountDetails(loadDetails);
      } else {
        setAccountDetails([]);
      }
    });

    // Fetch Account Groups for Dropdown
    const groupsRef = ref(database, 'Grampanchayat/Panipuravtha/AccountGroups');
    const unsubscribeGroups = onValue(groupsRef, (snapshot) => {
        const data = snapshot.val();
        if (data) {
          const loadGroups = Object.keys(data).map((key) => ({
            id: key,
            ...data[key],
          }));
          setAccountGroups(loadGroups);
        } else {
            setAccountGroups([]);
        }
    });

    return () => {
        unsubscribeDetails();
        unsubscribeGroups();
    };
  }, []);

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleGroupSelect = (group) => {
      setFormData(prev => ({
          ...prev,
          accountGroupId: group.id,
          accountGroupName: group.groupName
      }));
      setIsGroupModalOpen(false);
  }

  const handleSave = () => {
    if (!formData.accountName) {
      alert("Please fill in Account Name");
      return;
    }
    if (!formData.accountGroupName) {
        alert("Please select an Account Group");
        return;
    }


    if (isEditing && selectedId) {
      const detailRef = ref(database, `Grampanchayat/Panipuravtha/AccountDetails/${selectedId}`);
      update(detailRef, formData)
        .then(() => {
          alert("Data Updated Successfully!");
          resetForm();
        })
        .catch((error) => {
          console.error("Error updating data: ", error);
          alert("Error updating data");
        });
    } else {
      const detailsRef = ref(database, 'Grampanchayat/Panipuravtha/AccountDetails');
      push(detailsRef, formData)
        .then(() => {
            alert("Data Saved Successfully!");
            resetForm();
        })
        .catch((error) => {
          console.error("Error saving data: ", error);
          alert("Error saving data");
        });
    }
  };

  const resetForm = () => {
    setFormData(initialFormState);
    setIsEditing(false);
    setSelectedId(null);
    setView('list');
    setGroupSearchTerm('');
  };

  const handleEdit = (detail) => {
    setFormData(detail);
    setSelectedId(detail.id);
    setIsEditing(true);
    setView('form');
  };

  const handleDelete = (id) => {
    if (window.confirm("Are you sure you want to delete this record?")) {
      const detailRef = ref(database, `Grampanchayat/Panipuravtha/AccountDetails/${id}`);
      remove(detailRef)
        .then(() => {
          alert("Record deleted successfully");
        })
        .catch((error) => {
          console.error("Error deleting record: ", error);
          alert("Error deleting record");
        });
    }
  };

  const filteredDetails = accountDetails.filter(detail => 
    detail.accountName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    detail.accountNo?.toString().toLowerCase().includes(searchTerm.toLowerCase())
  );

  const filteredGroups = accountGroups.filter(group => 
    group.groupName?.toLowerCase().includes(groupSearchTerm.toLowerCase()) ||
    group.groupNo?.toString().includes(groupSearchTerm)
  );

  return (
    <div className="min-h-screen bg-gray-50 p-4 md:p-8 font-poppins relative">
      
      {/* Header */}
      <div className="max-w-7xl mx-auto mb-6">
        <div className="bg-[#267c77] rounded-lg shadow-sm p-4 flex justify-between items-center text-white">
            <div>
                 <h1 className="text-xl font-bold">
                    खाते माहिती
                 </h1>
            </div>
            <div className="flex gap-2">
                 <button 
                   onClick={() => navigate('/panipuravtha/permanent-info/account-info', { state: location.state })}
                   className="px-4 py-2 bg-gray-500 text-white rounded hover:bg-gray-600 transition"
                 >
                   मागे जा
                 </button>
                 {view === 'list' && (
                   <button 
                     onClick={() => {
                        resetForm();
                        setView('form');
                     }}
                     className="px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700 transition"
                   >
                     नविन
                   </button>
                 )}
            </div>
        </div>
      </div>

      {view === 'list' ? (
        /* LIST VIEW */
        <div className="max-w-7xl mx-auto bg-white rounded-lg shadow-md overflow-hidden">
          
          {/* Search Bar */}
          <div className="p-4 border-b border-gray-200">
             <div className="flex gap-4">
                <div className="relative flex-1">
                    <input 
                    type="text" 
                    placeholder="खाते नं. / खाते नाव" 
                    className="w-full px-4 py-2 border border-[#267c77]/30 rounded focus:outline-none focus:ring-2 focus:ring-[#267c77]"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    />
                </div>
                <button className="px-6 py-2 bg-[#267c77] text-white rounded hover:bg-[#1e6b66] transition">
                    शोधणे
                </button>
             </div>
          </div>

          {/* Table */}
          <div className="overflow-x-auto">
            <table className="w-full text-left border-collapse">
              <thead>
                <tr className="bg-[#267c77] text-white">
                  <th className="p-3 border-r border-white/30 font-medium text-center">खाते क्र.</th>
                  <th className="p-3 border-r border-white/30 font-medium">खाते नाव</th>
                  <th className="p-3 border-r border-white/30 font-medium">खाते गट</th>
                  <th className="p-3 border-r border-white/30 font-medium text-center">प्रकार</th>
                  <th className="p-3 border-r border-white/30 font-medium text-center leading-tight">व्यवहार<br/>परवानगी</th>
                  <th className="p-3 border-r border-white/30 font-medium text-center">अंदाजपत्रक</th>
                  <th className="p-3 font-medium text-center w-40">पर्याय</th>
                </tr>
              </thead>
              <tbody>
                  {filteredDetails.length > 0 ? (
                    filteredDetails.map((detail) => (
                      <tr key={detail.id} className="border-b hover:bg-gray-50">
                          <td className="p-3 text-center border-r">{detail.accountNo}</td>
                          <td className="p-3 border-r">{detail.accountName}</td>
                          <td className="p-3 border-r">{detail.accountGroupName}</td>
                          <td className="p-3 text-center border-r">{detail.type}</td>
                          <td className="p-3 text-center border-r">
                              <input type="checkbox" checked={detail.transactionPermission} readOnly className="w-4 h-4 text-[#267c77]" />
                          </td>
                          <td className="p-3 text-center border-r">
                              <input type="checkbox" checked={detail.budgetEntry} readOnly className="w-4 h-4 text-[#267c77]" />
                          </td>
                          <td className="p-3 text-center flex justify-center gap-2">
                               <button 
                                onClick={() => handleEdit(detail)}
                                className="px-3 py-1 bg-[#267c77]/80 text-white rounded hover:bg-[#267c77] text-sm font-medium"
                              >
                                बदल
                              </button>
                              <button 
                                onClick={() => handleDelete(detail.id)}
                                className="px-3 py-1 bg-red-600 text-white rounded hover:bg-red-700 text-sm font-medium"
                              >
                                खोडणे
                              </button>
                          </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                        <td colSpan="7" className="p-4 text-center text-gray-500 bg-white">
                            No records found
                        </td>
                    </tr>
                  )}
              </tbody>
            </table>
          </div>

           {/* Pagination */}
           <div className="p-2 border-t bg-gradient-to-r from-blue-700 to-blue-900 text-white flex justify-center items-center gap-2">
                <button className="px-2 py-1 bg-white/20 rounded hover:bg-white/30 text-sm">|&lt;</button>
                <button className="px-2 py-1 bg-white/20 rounded hover:bg-white/30 text-sm">&lt;&lt;</button>
                <span className="px-3 py-1 bg-white/20 rounded text-sm bg-[#1e6b66]">1</span>
                <span className="px-3 py-1 bg-white/20 rounded text-sm hover:bg-white/30 cursor-pointer">...</span>
                <button className="px-2 py-1 bg-white/20 rounded hover:bg-white/30 text-sm">&gt;&gt;</button>
                <button className="px-2 py-1 bg-white/20 rounded hover:bg-white/30 text-sm">&gt;|</button>
                <select className="ml-2 bg-white text-black text-sm p-1 rounded">
                    <option>5</option>
                    <option>10</option>
                    <option>20</option>
                </select>
           </div>
        </div>
      ) : (
        /* FORM VIEW */
        <div className="max-w-4xl mx-auto bg-white rounded-lg shadow-md overflow-hidden">
             
            <div className="p-8 space-y-6">
                
                <div className="grid grid-cols-12 gap-4 items-center">
                    <label className="col-span-12 md:col-span-2 text-gray-700 font-medium">प्रकार:</label>
                    <div className="col-span-12 md:col-span-10">
                        <select 
                            name="type" 
                            value={formData.type} 
                            onChange={handleInputChange} 
                            className="w-full md:w-1/2 p-2 border border-[#267c77]/30 rounded focus:outline-none focus:ring-[#267c77]"
                        >
                            <option value="मालमत्ता">मालमत्ता</option>
                            <option value="देणी">देणी</option>
                            <option value="उत्पन्न">उत्पन्न</option>
                            <option value="खर्च">खर्च</option>
                        </select>
                    </div>
                </div>

                <div className="grid grid-cols-12 gap-4 items-center">
                    <label className="col-span-12 md:col-span-2 text-gray-700 font-medium">खाते क्र.:</label>
                    <div className="col-span-12 md:col-span-4">
                        <input 
                            type="text" 
                            name="accountNo" 
                            value={formData.accountNo} 
                            onChange={handleInputChange} 
                            className="w-full p-2 border border-[#267c77]/30 rounded bg-gray-100 focus:outline-none"
                            placeholder="खाते क्र."
                        />
                    </div>
                </div>

                <div className="grid grid-cols-12 gap-4 items-center">
                    <label className="col-span-12 md:col-span-2 text-gray-700 font-medium">खाते नाव:</label>
                    <div className="col-span-12 md:col-span-10">
                        <input 
                            type="text" 
                            name="accountName" 
                            value={formData.accountName} 
                            onChange={handleInputChange} 
                            className="w-full p-2 border border-[#267c77]/30 rounded focus:outline-none focus:ring-1 focus:ring-[#267c77]"
                            placeholder="Please Enter LedgerName"
                        />
                    </div>
                </div>

                <div className="grid grid-cols-12 gap-4 items-center">
                    <label className="col-span-12 md:col-span-2 text-gray-700 font-medium">खाते गट:</label>
                    <div className="col-span-12 md:col-span-8">
                         <input 
                            type="text" 
                            name="accountGroupName"
                            value={formData.accountGroupName} 
                            readOnly
                            placeholder="खातावणी गट निवडा."
                            className="w-full p-2 border border-[#267c77]/30 rounded bg-gray-100 focus:outline-none cursor-pointer"
                            onClick={() => setIsGroupModalOpen(true)}
                        />
                    </div>
                     <div className="col-span-12 md:col-span-2">
                         <button 
                            className="w-full p-2 bg-[#267c77] text-white rounded hover:bg-[#1e6b66]"
                            onClick={() => setIsGroupModalOpen(true)}
                        >
                            ...
                        </button>
                     </div>
                </div>

                <div className="grid grid-cols-12 gap-8 items-center mt-6">
                    <div className="col-span-12 md:col-span-6 flex items-center gap-4">
                         <label className="text-gray-700 font-medium">व्यवहार परवानगी :</label>
                         <input 
                            type="checkbox" 
                            name="transactionPermission" 
                            checked={formData.transactionPermission} 
                            onChange={handleInputChange} 
                            className="w-6 h-6 border-[#267c77]/30 text-[#267c77] focus:ring-[#267c77]" 
                        />
                    </div>
                    <div className="col-span-12 md:col-span-6 flex items-center gap-4 justify-end">
                         <label className="text-gray-700 font-medium">अंदाजपत्रकात नोंद आहे का:</label>
                         <input 
                            type="checkbox" 
                            name="budgetEntry" 
                            checked={formData.budgetEntry} 
                            onChange={handleInputChange} 
                            className="w-6 h-6 border-[#267c77]/30 text-[#267c77] focus:ring-[#267c77]" 
                        />
                    </div>
                </div>
                
                {/* Permission Table Portion */}
                <div className="mt-8">
                    <label className="block text-gray-700 font-medium mb-2">विभाग परवानगी:</label>
                    <div className="border border-[#267c77]/30">
                        <div className="grid grid-cols-12 bg-[#267c77] text-white text-center py-2">
                            <div className="col-span-2 border-r border-white/30">परवानगी</div>
                            <div className="col-span-3 border-r border-white/30">खाते क्र.</div>
                            <div className="col-span-4 border-r border-white/30">आरंभीची शिल्लक</div>
                            <div className="col-span-1 border-r border-white/30">प्रकार</div>
                            <div className="col-span-2">शिल्लक<br/>प्रकार</div>
                        </div>
                        <div className="grid grid-cols-12 text-center border-t border-[#267c77]/20">
                             <div className="col-span-2 border-r border-[#267c77]/20 p-2 flex justify-center items-center">
                                 <input 
                                    type="checkbox" 
                                    name="panipuravthaPermission" 
                                    checked={formData.panipuravthaPermission} 
                                    onChange={handleInputChange} 
                                    className="w-6 h-6 text-[#267c77]"
                                />
                             </div>
                             <div className="col-span-3 border-r border-[#267c77]/20 p-2 flex items-center justify-start px-4">
                                 पाणीपुरवठा
                             </div>
                             <div className="col-span-4 border-r border-[#267c77]/20 p-2">
                                 <input 
                                    type="number" 
                                    name="openingBalance"
                                    value={formData.openingBalance}
                                    onChange={handleInputChange}
                                    className="w-full p-1 border border-[#267c77]/30 rounded"
                                />
                             </div>
                             <div className="col-span-1 border-r border-[#267c77]/20 p-2 flex justify-center items-center">
                                  <input 
                                    type="checkbox" 
                                    name="isDebit" 
                                    checked={formData.isDebit || false} 
                                    onChange={handleInputChange} 
                                    className="w-5 h-5 cursor-pointer text-[#267c77]" 
                                />
                             </div>
                             <div className="col-span-2 p-2 flex justify-center items-center font-medium">
                                 {formData.isDebit ? 'नावे' : 'जमा'}
                             </div>
                        </div>
                    </div>
                </div>

            </div>

             {/* Footer Buttons */}
             <div className="flex justify-center gap-4 py-4 bg-blue-800 mt-4">
                <button 
                  onClick={handleSave}
                  className="px-6 py-2 bg-[#267c77] text-white rounded hover:bg-[#1e6b66] transition font-medium"
                >
                    साठविणे
                </button>
                <button 
                  onClick={() => setView('list')}
                  className="px-6 py-2 bg-red-600 text-white rounded hover:bg-red-700 transition font-medium"
                >
                    रद्द करणे
                </button>
             </div>
        </div>
      )}

      {/* Account Group Selection Modal */}
      {isGroupModalOpen && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
              <div className="bg-white rounded-lg shadow-lg w-full max-w-2xl mx-4 overflow-hidden">
                  <div className="bg-[#267c77] text-white p-3 flex justify-between items-center">
                      <h3 className="text-lg font-medium">खातावणी गट माहिती</h3>
                      <button onClick={() => setIsGroupModalOpen(false)} className="hover:text-red-300">
                          <X size={24} />
                      </button>
                  </div>
                  <div className="p-4">
                      {/* Search */}
                      <div className="mb-4">
                          <label className="block text-gray-700 mb-1">शोधणे</label>
                          <input 
                            type="text"
                            placeholder="खातावणी गटाचे नाव" 
                            className="w-full p-2 border border-[#267c77]/30 rounded"
                            value={groupSearchTerm}
                            onChange={(e) => setGroupSearchTerm(e.target.value)}
                         />
                      </div>
                      
                      {/* Table */}
                      <div className="border border-[#267c77]/30 max-h-64 overflow-y-auto">
                          <table className="w-full text-left">
                              <thead className="bg-[#267c77] text-white sticky top-0">
                                  <tr>
                                      <th className="p-2 border-r border-white/30 w-24">गट नं.</th>
                                      <th className="p-2">गटाचे नाव</th>
                                  </tr>
                              </thead>
                              <tbody>
                                  {filteredGroups.length > 0 ? (
                                      filteredGroups.map(group => (
                                          <tr 
                                            key={group.id} 
                                            onClick={() => handleGroupSelect(group)}
                                            className="cursor-pointer hover:bg-gray-50 border-b border-gray-200"
                                          >
                                              <td className="p-2 border-r border-gray-300">{group.groupNo}</td>
                                              <td className="p-2">{group.groupName}</td>
                                          </tr>
                                      ))
                                  ) : (
                                      <tr>
                                          <td colSpan="2" className="p-4 text-center text-gray-500">
                                              No records found
                                          </td>
                                      </tr>
                                  )}
                              </tbody>
                          </table>
                      </div>
                  </div>
              </div>
          </div>
      )}

    </div>
  );
};

export default AccountDetails;
